import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { PrimeNgModule } from '../primeng/primeng.module';
import { BookingSchedule } from 'src/app/simulator-booking/models/booking-schedule';
import { Simulator } from 'src/app/simulator-booking/models/simulators';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BookingWidgetService } from '../booking-widget/booking-widget.service';
import { DatePipe } from '@angular/common';
import { StripeElementsDirective, StripePaymentElementComponent, StripeService, injectStripe } from 'ngx-stripe';
import { StripeElementsOptions, StripePaymentElementOptions } from '@stripe/stripe-js';
import { environment } from 'src/environments/environment';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AlertDialogPrimeNg } from '../primeng/dialogs/alert.dialog';
import { BookingStatusEnum } from 'src/app/shared/models/booking-status.enum';
import { CreditCardFeeType } from 'src/app/shared/models/creditcard-fee-type.enum';
import { Addon } from 'src/app/simulator-booking/models/addon';
import { OperatingHours } from 'src/app/simulator-booking/models/operating-hours';
import { DigitalWaiverWidgetService } from '../digital-waiver-widget/digital-waiver-widget.service';
import { DigitalWaiver } from 'src/app/simulator-booking/models/digital-waiver';
import { SharedModule } from 'src/app/shared/shared.module';
import { StringHelpers } from 'src/app/shared/helpers/string-helpers';

@Component({
  selector: 'booking-membership-widget',
  imports: [
    SharedModule,
    DatePipe,
    StripeElementsDirective,
    StripePaymentElementComponent
  ],
  templateUrl: './booking-membership-widget.component.html',
  styleUrl: './booking-membership-widget.component.scss',
})
export class BookingMembershipWidgetComponent implements OnInit {
  readonly stripe = injectStripe();

  @ViewChild(StripeElementsDirective) elements!: StripeElementsDirective;
  @ViewChild(StripePaymentElementComponent) paymentElement!: StripePaymentElementComponent;

  public elementsOptions: StripeElementsOptions = {
    appearance: {
      theme: 'stripe'
    },
    locale: 'en',
  };

  public paymentElementsOptions: StripePaymentElementOptions = {
    layout: {
      type: 'tabs',
      defaultCollapsed: false,
    }
  };

  dialogRef: DynamicDialogRef;

  public stripePaymentIntentData: any;
  private paymentIntent: any;

  public simulators: Simulator[] = [];
  public availableTimeSlots: any[] = [];
  public availableAddons: Addon[] = [];
  public booking: BookingSchedule = new BookingSchedule();
  private apiKey: string;
  private destroyed$ = new Subject<void>();
  public messages = [];
  public userDetails: any;
  private activeDigitalWaiver: DigitalWaiver;
  public minDate: Date | undefined;
  public maxDate: Date | undefined;

  public selectedDate: Date | undefined;
  public selectedSimulator: Simulator;
  public selectedDuration: number;
  public selectedTimeSlot: any;
  public validator: string = '';
  public notes: string;
  public participants: number;
  public selectedAddons: Addon[] = [];
  private customerResponse: any;
  public loadingStates = {
    page: false,
    timeSlots: false,
    payment: false,
    submission: false,
    membershipValidation: false
  };

  public totalPrice: number = 0;
  public validatedMembership: any = null;
  public isCardValid: boolean = false;
  route = inject(ActivatedRoute);

  constructor(private bookingWidgetService: BookingWidgetService,
    protected stringHelpers: StringHelpers,
    private ngxStripeService: StripeService,
    private dialogService: DialogService,
    private digitalWaiverWidgetService: DigitalWaiverWidgetService) {
  }

  ngOnInit(): void {
    this.loadingStates.page = true;
    this.route.queryParamMap.pipe(takeUntil(this.destroyed$)).subscribe((params: any) => {
      this.apiKey = params.get('apiKey');
      this.getUserOrganization();

      // Set min date to today
      this.minDate = new Date();

      // Set max date to last day of next month
      const today = new Date();
      this.maxDate = new Date(today.getFullYear(), today.getMonth() + 2, 0);

      this.destroyed$.unsubscribe();
    });
  }

  getActiveDigitalWaiver() {
    this.digitalWaiverWidgetService.getActiveDigitalWaiver(this.userDetails.organization.id, this.apiKey).subscribe({
      next: (result: any) => {
        this.activeDigitalWaiver = result;
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  getUserOrganization() {
    this.bookingWidgetService.getUserOrganization(this.apiKey).subscribe({
      next: (result) => {
        this.userDetails = result;
        this.getAddons();
        this.getActiveDigitalWaiver();
      },
      error: (error) => {
        console.log("user account error");
        console.log(error);
        this.setMessage("Unable to get User Organization Details");
        this.loadingStates.page = false;
      },
      complete: () => this.getSimulators()
    });
  }

  getSimulators() {
    let data = { organizationId: this.userDetails.organization.id };
    this.bookingWidgetService.getSimulators(this.apiKey, data).subscribe({
      next: (result) => {
        this.simulators = result.results.filter(x => x.isAvailable === true);
      },
      error: (error) => {
        console.error(error);
        this.setMessage(error.error.Error);
        this.loadingStates.page = false;
      },
      complete: () => this.loadingStates.page = false
    })
  }

  getAvailableTimeSlots() {
    if (this.selectedDuration !== undefined) {
      this.loadingStates.timeSlots = true;
      let data = {
        simulatorId: this.selectedSimulator.id,
        duration: this.selectedDuration,
        startDate: this.stringHelpers.normalizeDateTime(this.selectedDate),
        organizationId: this.userDetails.organization.id,
        operatingHours: this.userDetails.organization.operatingHours ?
          JSON.parse(this.userDetails.organization.operatingHours as unknown as string) as OperatingHours[] :
          [],
        timeZone: this.userDetails.organization.timeZone
      };

      this.bookingWidgetService.getAvailableTimeSlots(this.apiKey, data).subscribe({
        next: (result) => {
          this.availableTimeSlots = result;
        },
        error: (error) => {
          this.loadingStates.timeSlots = false;
          console.error(error);
          this.setMessage(error.error.Error);
        },
        complete: () => this.loadingStates.timeSlots = false
      });
    }
  }

  getAddons() {
    let data = { organizationId: this.userDetails.organization.id };
    this.bookingWidgetService.getAddons(this.apiKey, data).subscribe({
      next: (result) => {
        this.availableAddons = result.results;
      },
      error: (error) => {
        console.error(error);
        this.setMessage(error.error.Error);
      },
      complete: () => this.loadingStates.page = false
    });
  }

  validateMembershipDiscountCode() {
    if (!this.validator) return;
    this.messages = [];
    this.loadingStates.membershipValidation = true;
    let data = {
      validator: this.validator,
      organizationId: this.userDetails.organization.id
    };

    // Use the existing booking widget service with a membership-specific endpoint
    this.bookingWidgetService.validateMembershipDiscountCode(this.apiKey, data).subscribe({
      next: (result) => {
        this.validatedMembership = result;
        if (this.validatedMembership.status === 202) {
          this.setMessage(this.validatedMembership.message, 'error');
        } else {
          this.setMessage(this.validatedMembership.message, 'success');
        }
      },
      error: (error) => {
        this.validatedMembership = null;
        this.setMessage('There was a problem validating your membership discount code. Please try again or contact the establishment for assistance.');
        this.loadingStates.membershipValidation = false;
        console.error(error);
      },
      complete: () => {
        this.loadingStates.membershipValidation = false;
      }
    });
  }

  clearMembershipDiscountCode() {
    this.validator = '';
    this.validatedMembership = null;
    this.messages = [];
    this.calculateTotalPrice();
  }

  onCardChange(event: any): void {
    this.isCardValid = event.complete && !event.error;
  }

  async handleNext(activateCallback: any, step: number): Promise<number> {
    try {
      this.loadingStates.payment = true;

      // Calculate total price before creating payment intent
      this.calculateTotalPrice();

      const result = await this.createPaymentIntent() as { client_secret: string };
      // Initialize Stripe Elements right before showing the payment form
      this.ngxStripeService.changeKey(environment.stripe.token, {
        stripeAccount: this.userDetails.organization.stripeAccountId
      });

      this.elementsOptions = {
        appearance: { theme: 'stripe' },
        locale: 'en',
        clientSecret: result.client_secret.toString(),
      } as StripeElementsOptions;

      setTimeout(() => {
        this.loadingStates.payment = false;
        return activateCallback(step);
      }, 100);
    } catch (error) {
      this.loadingStates.payment = false;
      console.error('Error in payment setup:', error);
      this.openAlertDialog('Error', 'Unable to setup payment. Please try again.');
      return activateCallback(step);
    }
  }

  async handleCancelPaymentBack(activateCallback: any, prevCallbackNumber: number): Promise<number> {
    return this.bookingWidgetService.cancelPaymentIntent(this.apiKey, {
      connectedAccountId: this.userDetails.organization.stripeAccountId,
      paymentIntentId: this.paymentIntent.id
    }).subscribe({
      next: (result) => {
        this.elementsOptions = {
          appearance: { theme: 'stripe' },
          locale: 'en',
        } as StripeElementsOptions;
        return activateCallback(prevCallbackNumber);
      },
      error: (error) => {
        console.error(error);
        this.setMessage(error);
        return activateCallback(prevCallbackNumber);
      }
    });
  }

  private calculateTotalPrice(): void {
    // Calculate base price (timeslot + addons)
    let basePrice = this.selectedTimeSlot?.price || 0;
    if (this.selectedAddons?.length > 0) {
      basePrice += this.selectedAddons.reduce((sum, addon) => sum + addon.price, 0);
    }

    // Add credit card fee
    this.totalPrice = this.userDetails?.organization?.creditCardFeeType === CreditCardFeeType.Percentage ?
      basePrice * (1 + this.userDetails.organization.creditCardFee / 100) :
      basePrice + this.userDetails.organization.creditCardFee;
      const discount = this.validatedMembership.membershipCustomer.discountPercent || 0;
      this.totalPrice = this.totalPrice * (1 - discount / 100);
  }

  async createPaymentIntent() {
    this.loadingStates.payment = true;

    let data = {
      accountId: this.userDetails.organization.stripeAccountId,
      options: {
        amount: Math.round(this.totalPrice * 100), // Convert to cents
        currency: 'usd',
        customer: this.validatedMembership.membershipCustomer.stripeCustomerId,
        setup_future_usage: 'off_session',
        metadata: {
          Source: 'Booking Membership Widget',
          UserId: this.userDetails.id,
          Parent: 'true'
        }
      }
    }

    return new Promise((resolve, reject) => {
      this.bookingWidgetService.createPaymentIntent(this.apiKey, data).subscribe({
        next: (result: any) => {
          this.paymentIntent = result;
          this.loadingStates.payment = false;
          resolve(result);
        },
        error: (err) => {
          this.loadingStates.payment = false;
          console.error(err);
          reject(err);
        }
      });
    });
  }

  submit() {
    if (!this.paymentElement?.elements) {
      this.openAlertDialog('Payment Error', 'Payment form not initialized');
      return;
    }

    this.loadingStates.submission = true;
    this.stripe.confirmPayment({
      elements: this.paymentElement.elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required'
    }).subscribe({
      next: (result) => {
        if (result.error) {
          this.openAlertDialog('Payment Error', `There was an error processing your payment. Please try again or contact the establishment for assistance.`);
        } else {
          if (result.paymentIntent.status === 'succeeded') {
            this.stripePaymentIntentData = { hasPaid: true };  // Set the payment status
            this.createBooking();
          } else {
            this.openAlertDialog('Payment Unsuccessful', result.paymentIntent.status.toString());
          }
        }
      },
      error: (err) => {
        this.loadingStates.submission = false;
        this.openAlertDialog('Payment Error', `There was an error processing your payment. Please try again. \n ${err}`);
        console.log(err);
      }
    });
  }

  private createBooking() {
    this.loadingStates.submission = true;
    let data = {
      startTime: this.stringHelpers.normalizeDateTime(this.selectedTimeSlot.startTime),
      endTime: this.stringHelpers.normalizeDateTime(this.selectedTimeSlot.endTime),
      status: BookingStatusEnum.Booked,
      simulatorId: this.selectedSimulator.id,
      participants: this.participants,
      totalPrice: this.totalPrice,
      hasPaid: true,
      phoneNumber: this.validatedMembership.membershipCustomer.phoneNumber || '',
      email: this.validatedMembership.membershipCustomer.emailAddress,
      customerName: `${this.validatedMembership.membershipCustomer.firstName} ${this.validatedMembership.membershipCustomer.lastName}`,
      organizationId: this.userDetails.organization.id,
      createdBy: this.userDetails.id,
      organization: this.userDetails.organization.organizationName,
      bay: this.selectedSimulator.bayName,
      addons: this.selectedAddons,
      membershipDiscountCode: this.validator,
      membershipCustomerId: this.validatedMembership.membershipCustomer.id,
      notes: this.notes,
      stripePaymentIntentId: this.paymentIntent.id,
      timeZone: this.userDetails.organization.timeZone
    };

    this.bookingWidgetService.createBookingMembership(this.apiKey, data).subscribe({
      next: (result) => {
        if (result.status === 200 || result.status === 202) {
          if (this.activeDigitalWaiver !== undefined) {
            let message = '';
            if (result.status === 202) {
              message = 'Your booking has been confirmed but there was a problem sending your digital waiver and confirmation email. Please check with establishment for your confirmation number and digital waiver.';
            } else {
              message = 'Your booking has been confirmed. Your confirmation number and digital waiver will be sent to your email shortly.';
            }
            this.createDigitalWaiverEntry(result.booking.id);
            this.openAlertDialog('Payment Successful', message);
          } else {
            this.openAlertDialog('Payment Successful', 'Your booking has been confirmed and your confirmation number will be sent to your email shortly.');
          }
          this.bookingWidgetService.updatePaymentIntent(this.apiKey, {
            paymentIntentId: this.paymentIntent.id,
            accountId: this.userDetails.organization.stripeAccountId,
            options: {
              metadata: {
                BookingId: result.booking.id
              }
            }
          }).subscribe();
          this.dialogRef.onClose.subscribe(() => {
            window.location.reload();
          });
        }
        this.loadingStates.submission = false;
      },
      error: (error) => {
        this.loadingStates.submission = false;
        console.error(error);
        this.setMessage(error);
      }
    });
  }

  private createDigitalWaiverEntry(bookingId: string) {
    let now = new Date();
    const nowUtc = new Date(Date.UTC(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      now.getHours(),
      now.getMinutes()
    ));

    let data = {
      digitalWaiverId: this.activeDigitalWaiver.id,
      bookingId: bookingId,
      organizationId: this.userDetails.organization.id,
      firstName: this.validatedMembership.membershipCustomer.firstName,
      lastName: this.validatedMembership.membershipCustomer.lastName,
      emailAddress: this.validatedMembership.membershipCustomer.emailAddress,
      createdBy: this.userDetails.id,
      sentDate: nowUtc
    }

    this.digitalWaiverWidgetService.createDigitalWaiverEntry(this.apiKey, data).subscribe();
  }

  private openAlertDialog(title, body) {
    this.dialogRef = this.dialogService.open(AlertDialogPrimeNg, {
      header: title,
      width: '50vw',
      modal: true,
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      },
      data: {
        body: body,
        buttonText: 'Close'
      }
    });

    this.dialogRef.onClose.subscribe(() => {
      if (title.toLowerCase() === 'Success'.toLowerCase()) {
        // Reload the iframe after dialog closes
        window.location.reload();
      }
    });
  }

  isValid(properties: any[]): boolean {
    for (const property of properties) {
      if (property === null || property === undefined || property == '') {
        return false;
      }
    }

    return true;
  }

  private setMessage(message, severity = 'error') {
    this.messages.push({ severity: severity, summary: message });
  }

  
  normalizeDateTime(dateTime: any): string {
    try {
      // Parse the date/time value
      const date = new Date(dateTime);

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        throw new Error('Invalid date');
      }

      // Return ISO string without timezone info (just the local time components)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error('Error normalizing date/time:', error);
      // Fallback to current time if parsing fails
      return new Date().toISOString().slice(0, 19);
    }
  }
}
